import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { ChevronLeft, ChevronRight, MessageCircle, Calendar, Search, ClipboardList, CalendarCheck, Play, MessageSquare, Phone } from 'lucide-react';
import { MapPin, Wrench, Video, Heart } from 'lucide-react';
import { useTenant } from '../hooks/useTenant';
import { useCemeteryBranding, useCemeteryFeatures, useWelcomeScreenConfig, useCemeteryUIConfig } from '../hooks/useCemeteryConfig';
import { useSupabaseAsset } from '../hooks/useSupabaseAsset';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface WelcomeScreenProps {
  onGetStarted: () => void;
}

// Icon mapping
const iconMap = {
  MessageCircle,
  Calendar,
  Search,
  ClipboardList,
  CalendarCheck,
  Play,
  MessageSquare,
  Phone,
  MapPin,
  Wrench,
  Video,
  Heart,
};

// Robust Image Component with retry logic and cache busting
const RobustImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
}> = ({ src, alt, className, style, onLoad, onError }) => {
  const [imageSrc, setImageSrc] = React.useState(() => {
    // Add cache-busting parameter to force fresh fetches
    const cacheBuster = Date.now();
    return `${src}${src.includes('?') ? '&' : '?'}v=${cacheBuster}`;
  });
  const [retries, setRetries] = React.useState(0);
  const [hasError, setHasError] = React.useState(false);

  const handleError = React.useCallback(() => {
    console.error(`❌ Image failed to load (attempt ${retries + 1}):`, imageSrc);
    
    if (retries < 3) {
      // Retry with new cache-busted URL
      const newCacheBuster = Date.now() + Math.random();
      const retryUrl = `${src}${src.includes('?') ? '&' : '?'}v=${newCacheBuster}&retry=${retries + 1}`;
      
      console.log(`🔄 Retrying image load (${retries + 1}/3):`, retryUrl);
      
      setTimeout(() => {
        setImageSrc(retryUrl);
        setRetries(prev => prev + 1);
      }, 1000 * (retries + 1)); // Exponential backoff
    } else {
      console.error(`💥 Image failed after 3 retries:`, src);
      setHasError(true);
      onError?.();
    }
  }, [src, retries, imageSrc, onError]);

  const handleLoad = React.useCallback(() => {
    console.log(`✅ Image loaded successfully:`, imageSrc);
    setHasError(false);
    onLoad?.();
  }, [imageSrc, onLoad]);

  if (hasError) {
    return null; // Let parent handle fallback
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      onLoad={handleLoad}
      onError={handleError}
      crossOrigin="anonymous"
      referrerPolicy="no-referrer-when-downgrade"
    />
  );
};
// Loading skeleton component
const FeatureCardSkeleton: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-lg shadow-sm h-[240px] flex flex-col animate-pulse">
    {/* Top section skeleton (60%) */}
    <div className="h-[60%] bg-gray-200 rounded-t-lg"></div>
    
    {/* Bottom section skeleton (40%) */}
    <div 
      className="h-[40%] p-4"
      style={{
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.3)',
      }}
    >
      {/* Icon and title row skeleton */}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-5 h-5 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded flex-1"></div>
      </div>
      
      {/* Description skeleton */}
      <div className="space-y-1">
        <div className="h-3 bg-gray-200 rounded"></div>
        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
      </div>
    </div>
  </div>
);

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onGetStarted }) => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { data: branding, isLoading: brandingLoading, error: brandingError } = useCemeteryBranding(!!tenant?.id);
  const { data: features, isLoading: featuresLoading, error: featuresError } = useCemeteryFeatures(!!tenant?.id);
  const { data: welcomeConfig, isLoading: welcomeLoading, error: welcomeError } = useWelcomeScreenConfig(!!tenant?.id);
  const { data: uiConfig } = useCemeteryUIConfig(!!tenant?.id);
  
  // DEBUG: Log features and icon data
  console.log('🔍 DEBUG - Features from database:', features);
  console.log('🔍 DEBUG - Icon names:', features?.map(f => f.icon_name));
  console.log('🔍 DEBUG - Available icons:', Object.keys(iconMap));
  
  // DEBUG: Comprehensive feature and image debugging
  console.log('🎯 BOROONDARA DEBUG - Full features array:', features);
  if (features && features.length > 0) {
    console.log('🎯 BOROONDARA DEBUG - Number of features loaded:', features.length);
    features.forEach((feature, index) => {
      console.log(`🎯 FEATURE ${index + 1} DEBUG:`, {
        id: feature.id,
        feature_key: feature.feature_key,
        custom_title: feature.custom_title,
        background_image_url: feature.background_image_url,
        has_background_url: !!feature.background_image_url,
        url_type: typeof feature.background_image_url,
        url_length: feature.background_image_url?.length || 0,
        is_empty_string: feature.background_image_url === '',
        is_null: feature.background_image_url === null,
        is_undefined: feature.background_image_url === undefined,
        trimmed_url: feature.background_image_url?.trim(),
        will_show_image: !!(feature.background_image_url && feature.background_image_url.trim() !== '')
      });
    });
  } else {
    console.log('❌ BOROONDARA DEBUG - No features loaded or features is empty');
  }
  
  // DEBUG: Log welcome config data
  console.log('🔍 DEBUG - Welcome Screen Data:');
  console.log('  tenant:', tenant);
  console.log('  tenant.id:', tenant?.id);
  console.log('  tenant.name:', tenant?.name);
  console.log('  tenant.displayName:', tenant?.displayName);
  console.log('  welcomeConfig:', welcomeConfig);
  console.log('  welcomeConfig?.welcome_heading:', welcomeConfig?.welcome_heading);
  console.log('  welcomeConfig?.welcome_subheading:', welcomeConfig?.welcome_subheading);
  console.log('  welcomeConfig:', welcomeConfig);
  console.log('  welcomeConfig?.welcome_heading:', welcomeConfig?.welcome_heading);
  console.log('  welcomeConfig?.welcome_subheading:', welcomeConfig?.welcome_subheading);
  console.log('  welcomeLoading:', welcomeLoading);
  console.log('  welcomeError:', welcomeError);
  
  // Get logo from Supabase storage
  const logoPath = tenant?.id ? `logos/${tenant.id}-logo.png` : null;
  const { url: logoUrl, exists: logoExists, isLoading: logoLoading } = useSupabaseAsset(logoPath);
  
  const swiperRef = useRef<any>(null);

  const isLoading = brandingLoading || featuresLoading || welcomeLoading;

  // Log any errors
  if (brandingError) console.error('❌ Branding error:', brandingError);
  if (featuresError) console.error('❌ Features error:', featuresError);
  if (welcomeError) console.error('❌ Welcome config error:', welcomeError);

  const handleGetStarted = () => {
    navigate('/chat');
  };

  // Logo component with dynamic Supabase storage URL and fallback
  const renderLogo = () => {
    // Get logo height from config or use default
    const logoHeight = welcomeConfig?.logo_height || 'h-16';
    const tenantName = tenant?.name || tenant?.displayName || 'Cemetery Services';
    
    // Show loading state
    if (logoLoading) {
      return (
        <div className="flex justify-center mb-6">
          <div className={`${logoHeight} flex items-center justify-center`}>
            <div className="w-16 h-16 border-4 border-gray-200 border-t-[var(--color-primary,#EA6D2C)] rounded-full animate-spin"></div>
          </div>
        </div>
      );
    }
    
    // Show logo if it exists and loaded successfully
    if (logoUrl && logoExists) {
      return (
        <div className="flex justify-center mb-6">
          <img 
            src={logoUrl}
            alt={`${tenantName} Logo`}
            className="w-auto object-contain max-w-xs"
            style={{ 
             height: welcomeConfig?.logo_height || '128px'
            }}
            onError={(e) => {
              console.warn(`⚠️ Logo failed to load from: ${logoUrl}`);
              // Hide the image on error and show text fallback
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      );
    }
    
    // Fallback to text logo
    return (
      <div className="flex justify-center mb-6">
        <h1 className={`${logoHeight} flex items-center justify-center text-2xl md:text-3xl font-bold text-center`}
            style={{ color: 'var(--color-primary, #EA6D2C)' }}>
          {tenantName}
        </h1>
      </div>
    );
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary,#FAFAFA)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-[var(--color-primary,#EA6D2C)] rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-[var(--color-text-primary,#1F2937)] mb-2">
            Loading Cemetery
          </h2>
          <p className="text-[var(--color-text-secondary,#6B7280)]">
            Preparing your experience...
          </p>
        </div>
      </div>
    );
  }

  // Show error state if critical data is missing
  if (brandingError || featuresError || welcomeError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Configuration Error</h2>
          <p className="text-gray-600 mb-4">
            Unable to load cemetery configuration. Please check the database setup.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary,#FAFAFA)]">
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Welcome Content */}
        <div className="text-center mb-6 md:mb-12 pt-4">
          {/* Logo at top */}
          {renderLogo()}
          
          {/* Headline */}
          {(welcomeConfig?.show_headline !== false) && (
            <>
            <div className="mb-4 md:mb-6 mt-8 text-3xl sm:text-4xl md:text-5xl font-heading font-bold" style={{ color: 'var(--color-text-primary, #1F2937)' }}>
              {welcomeConfig?.welcome_heading || 'Welcome to Your Sanctuary'}
            </div>
            </>
          )}

          {/* Tagline */}
          {(welcomeConfig?.show_tagline !== false) && (
            <>
            <p
              className="font-body text-base md:text-lg max-w-2xl mx-auto mb-6"
              style={{ color: 'var(--color-text-secondary, #6B7280)' }}
            >
              {welcomeConfig?.welcome_subheading || 'Everything you need all in one place'}
            </p>
            </>
          )}
        </div>

        {/* Cards */}
        <div className="relative mb-8 px-4">
          <Swiper
            ref={swiperRef}
            modules={[Navigation, Pagination]}
            spaceBetween={12}
            pagination={{
              clickable: true,
              bulletClass: 'swiper-pagination-bullet',
              bulletActiveClass: 'swiper-pagination-bullet-active',
            }}
            navigation={{
              prevEl: '.swiper-button-prev-custom',
              nextEl: '.swiper-button-next-custom',
            }}
            breakpoints={{
              320: {
                slidesPerView: welcomeConfig?.cards_per_row_mobile || 1,
                spaceBetween: 16,
              },
              768: {
                slidesPerView: welcomeConfig?.cards_per_row_tablet || 2,
                spaceBetween: 16,
              },
              1024: {
                slidesPerView: Math.min(welcomeConfig?.cards_per_row_desktop || 3, features?.length || 3),
                spaceBetween: 16,
              },
              1280: {
                slidesPerView: Math.min(welcomeConfig?.cards_per_row_desktop || 4, features?.length || 4),
                spaceBetween: 16,
              },
            }}
            className="feature-carousel"
          >
            {featuresLoading ? (
              // Show skeleton loaders while loading
              Array.from({ length: 4 }).map((_, index) => (
                <SwiperSlide key={`skeleton-${index}`}>
                  <div>
                    <FeatureCardSkeleton />
                  </div>
                </SwiperSlide>
              ))
            ) : (
              features?.map((feature, index) => {
                const IconComponent = iconMap[feature.icon_name as keyof typeof iconMap] || MessageCircle;
                
                // Enhanced debug logging for each card
                console.log(`🎯 RENDERING CARD ${index + 1}:`, {
                  feature_key: feature.feature_key,
                  title: feature.custom_title,
                  background_url: feature.background_image_url,
                  will_render_image: !!(feature.background_image_url && feature.background_image_url.trim() !== ''),
                  rendering_fallback: !(feature.background_image_url && feature.background_image_url.trim() !== '')
                });
                
                return (
                  <SwiperSlide key={feature.id}>
                    <div 
                      className="bg-white rounded-lg shadow-md border border-gray-100 transition-all duration-200 hover:shadow-lg hover:border-gray-200 active:shadow-xl cursor-pointer h-[240px] flex flex-col overflow-hidden"
                      style={{
                        borderRadius: uiConfig?.border_radius || '8px',
                      }}
                    >
                      {/* Top Section - Image or Color Background (75% height) */}
                      <div 
                        className="h-[65%] rounded-t-lg relative"
                        style={{
                          borderTopLeftRadius: uiConfig?.border_radius || '8px',
                          borderTopRightRadius: uiConfig?.border_radius || '8px',
                        }}
                      >
                        {/* Always show what we're trying to render */}
                        
                        {/* Background Image or Color */}
                        {(() => {
                          const hasValidUrl = feature.background_image_url && feature.background_image_url.trim() !== '';
                          console.log(`🎨 CARD ${index + 1} IMAGE DECISION:`, {
                            hasValidUrl,
                            url: feature.background_image_url,
                            willShowImage: hasValidUrl
                          });
                          
                          if (hasValidUrl) {
                            console.log(`🖼️ ATTEMPTING TO LOAD IMAGE ${index + 1}:`, feature.background_image_url);
                            return (
                              <RobustImage
                                src={feature.background_image_url}
                                alt={`${feature.custom_title} background`}
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{
                                  borderTopLeftRadius: uiConfig?.border_radius || '8px',
                                  borderTopRightRadius: uiConfig?.border_radius || '8px',
                                }}
                                onLoad={() => {
                                  console.log(`✅ SUCCESS: Image ${index + 1} loaded successfully for ${feature.feature_key}:`, feature.background_image_url);
                                }}
                                onError={(e) => {
                                  console.log(`💥 FINAL FAILURE: Image ${index + 1} failed after all retries for ${feature.feature_key}`);
                                }}
                              />
                            );
                          } else {
                            console.log(`🎨 FALLBACK: Using gradient for card ${index + 1} (${feature.feature_key}) - no valid URL`);
                            return (
                              <div
                               className={`w-full h-full bg-gradient-to-br from-green-400 to-green-600`}
                                style={{
                                  borderTopLeftRadius: uiConfig?.border_radius || '8px',
                                  borderTopRightRadius: uiConfig?.border_radius || '8px',
                                }}
                             >
                               <div className="w-full h-full flex items-center justify-center opacity-20">
                                 <div className="text-white text-4xl font-bold">{feature.custom_title.charAt(0)}</div>
                               </div>
                             </div>
                            );
                          }
                        })()}
                      </div>
                      
                      {/* Bottom Section - Glassmorphic Content Area (35% height) */}
                      <div 
                        className="h-[35%] p-4 relative z-10 flex flex-col justify-between"
                        style={{
                          background: 'rgba(245, 245, 240, 0.8)',
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)',
                          borderTop: '1px solid rgba(255, 255, 255, 0.3)',
                        }}
                      >
                        <div className="flex-1">
                          {/* Icon and Title Row */}
                          <div className="flex items-center gap-2 mb-2">
                            <IconComponent 
                              className="w-5 h-5 flex-shrink-0"
                              style={{ color: 'var(--color-primary, #EA6D2C)' }}
                            />
                            <h3 className="font-heading text-base md:text-base font-semibold text-gray-900 leading-tight">
                              {feature.custom_title}
                            </h3>
                          </div>
                          
                          {/* Description */}
                          <p 
                            className="font-body text-sm md:text-xs leading-tight line-clamp-2"
                            style={{ 
                             color: feature.text_color_override || '#374151', // Use darker gray (#374151 = gray-700) as default
                              display: 'block',
                              visibility: 'visible',
                              opacity: 1
                            }}
                          >
                            {feature.custom_description || `Feature: ${feature.feature_key}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })
            )}
          </Swiper>

          {/* Custom Navigation Buttons (Hidden on Mobile) */}
          <button 
            className="swiper-button-prev-custom hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white border rounded-full items-center justify-center shadow-sm hover:shadow-md transition-shadow"
            style={{ 
              borderColor: 'var(--color-gray-200, #E5E7EB)',
              borderRadius: uiConfig?.border_radius || '50%',
            }}
            onClick={() => swiperRef.current?.swiper?.slidePrev()}
          >
            <ChevronLeft 
              className="w-5 h-5"
              style={{ color: 'var(--color-text-secondary, #6B7280)' }}
            />
          </button>
          
          <button 
            className="swiper-button-next-custom hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white border rounded-full items-center justify-center shadow-sm hover:shadow-md transition-shadow"
            style={{ 
              borderColor: 'var(--color-gray-200, #E5E7EB)',
              borderRadius: uiConfig?.border_radius || '50%',
            }}
            onClick={() => swiperRef.current?.swiper?.slideNext()}
          >
            <ChevronRight 
              className="w-5 h-5"
              style={{ color: 'var(--color-text-secondary, #6B7280)' }}
            />
          </button>
        </div>

        {/* Get Started Button */}
        <div className="text-center mt-8">
          <button
            onClick={handleGetStarted}
            className="w-full md:w-auto md:min-w-[300px] h-12 px-8 text-white font-body font-medium transition-all duration-200 transform hover:scale-105"
            style={{
              backgroundColor: 'var(--color-primary, #EA6D2C)',
              borderRadius: uiConfig?.border_radius || '8px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-primary-hover, #DB5A1F)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-primary, #EA6D2C)';
            }}
          >
            {welcomeConfig?.get_started_button_text || 'Get Started'}
          </button>
        </div>
      </div>

      {/* Custom Pagination Styles */}
      <style jsx>{`
        .feature-carousel .swiper-pagination {
          position: static;
          margin-top: 0.25rem;
        }
        
        .feature-carousel .swiper-pagination-bullet {
          width: 8px;
          height: 8px;
          background: var(--color-gray-300, #D1D5DB);
          opacity: 1;
          margin: 0 4px;
        }
        
        .feature-carousel .swiper-pagination-bullet-active {
          background: var(--color-primary, #EA6D2C);
        }
      `}</style>
    </div>
  );
};

export default WelcomeScreen;